import { Link } from '@tanstack/react-router'
import { AlertTriangle, DollarSign } from 'lucide-react'
import type { ProjectApCostSummary } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AmountList, type AmountRow } from '@/features/projects/amount-list'
import { ProjectFreightCosts } from '@/features/projects/project-freight-costs'
import { ProjectCustomerPayments } from '@/features/quote-builder/invoicing/project-payments-card'
import { formatMoney } from '@/lib/utils'

const INVOICE_LIMIT = 6

/** Supplier cost rows; Committed, Invoiced and Cash paid always show, the rest only when not zero. */
function supplierRows(costs: ProjectApCostSummary): AmountRow[] {
  const rows: (AmountRow & { always?: boolean })[] = [
    { label: 'Committed', amount: costs.committed_po_amount, strong: true, always: true },
    { label: 'Invoiced', amount: costs.invoiced_amount, always: true },
    { label: 'Approved', amount: costs.approved_amount },
    { label: 'Cash paid', amount: costs.cash_paid_amount, always: true },
    { label: 'Credits used', amount: costs.credit_applied_amount },
    { label: 'Open credits', amount: costs.open_credit_amount },
    { label: 'Held/disputed', amount: costs.held_disputed_amount },
    { label: 'Known variance', amount: costs.known_variance },
    { label: 'PO-backed', amount: costs.po_backed_invoice_amount },
    { label: 'Non-PO', amount: costs.non_po_invoice_amount },
  ]
  return rows
    .filter((row) => row.always || Number(row.amount) !== 0)
    .map((row) => ({ label: row.label, amount: row.amount, strong: row.strong }))
}

function SupplierCosts({
  costs,
  isLoading,
  isError,
}: {
  costs: ProjectApCostSummary | undefined
  isLoading: boolean
  isError: boolean
}) {
  return (
    <section aria-labelledby="project-supplier-costs" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 id="project-supplier-costs" className="text-xs font-medium text-muted-foreground">
          Suppliers
        </h4>
        <Link to="/invoices" className="shrink-0 text-xs font-medium text-primary hover:underline">
          Open AP
        </Link>
      </div>
      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-4 w-full" />
          ))}
        </div>
      ) : isError || !costs ? (
        <p className="text-sm text-destructive">AP cost context is temporarily unavailable.</p>
      ) : (
        <>
          <AmountList rows={supplierRows(costs)} />
          <ProjectFreightCosts costs={costs} />
          {costs.warnings.map((warning) => (
            <p
              key={warning}
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {warning}
            </p>
          ))}
          {costs.invoices.length > 0 && (
            <div className="space-y-0.5 border-t pt-2">
              {costs.invoices.slice(0, INVOICE_LIMIT).map((invoice) => (
                <Link
                  key={invoice.invoice_uuid}
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: invoice.invoice_uuid }}
                  className="-mx-2 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent"
                >
                  <span className="min-w-0 truncate font-medium">
                    {invoice.vendor_name || 'Vendor'} · {invoice.invoice_number || 'No invoice #'}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatMoney(invoice.invoice_amount)}
                  </span>
                  {invoice.allocation_required && <Badge variant="outline">Allocate</Badge>}
                </Link>
              ))}
              {costs.invoices.length > INVOICE_LIMIT && (
                <Link to="/invoices" className="block text-xs text-primary hover:underline">
                  View {costs.invoices.length - INVOICE_LIMIT} more
                </Link>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}

/** The project's money in one card: what the customer paid us, and what we owe suppliers. */
export function ProjectMoneyCard({
  projectId,
  apCosts,
  apCostsLoading,
  apCostsError,
}: {
  projectId: string
  apCosts: ProjectApCostSummary | undefined
  apCostsLoading: boolean
  apCostsError: boolean
}) {
  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-4 p-4">
        <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <DollarSign className="h-3.5 w-3.5" />
          Payments
        </h3>
        <ProjectCustomerPayments projectUuid={projectId} />
        <div className="border-t pt-4">
          <SupplierCosts costs={apCosts} isLoading={apCostsLoading} isError={apCostsError} />
        </div>
      </CardContent>
    </Card>
  )
}
